// src/services/chatTranslateAPI.js
//
// Ask Previu in Malayalam / Hindi: POST /api/chat/translate, called around the
// normal chat request — "in" turns the patient's message into English before
// it is sent, "out" turns each finished English reply bubble into ml / hi.
//
// Deliberately a bare fetch, not the axios client in api.js: that client's
// 401/403 interceptor logs the patient out, and a translation failure must
// never do that. The window.fetch session guard (utils/sessionExpiry.js) would
// do the same, so this path is on its NEVER_ENDS_SESSION list. Every failure — any non-2xx (401, 403, 404, 423, 429, 5xx),
// a network error, a body that isn't JSON, or no answer within 8 s — comes
// back as { ok:false, fallback:true } and the chat carries on in the original
// text. A session that has really expired is still caught by the chat
// request itself (chatService.js).

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

const TIMEOUT_MS = 8000;

const fallback = (reason) => ({ ok: false, fallback: true, reason });

// flags[] on a translation, as the backend (TranslationService +
// TranslationSafetyCheck) words them. Two kinds:
//
//   SAFETY — the translation may have changed the meaning; the patient is
//   asked to check it against the English. Free text, keyed by its first
//   words: "NUMBER …", "UNIT …", "FREQUENCY …", "MEAL TIMING …",
//   "DURATION …", "NEGATION …", "PERSON …", "NO TRANSLATION", and
//   IDENTIFIER_NOT_RESTORED.
//
//   HANDLING — notes on how the server got there, not a doubt about the
//   result: ESCALATION_KEPT_IN_ENGLISH, PLACEHOLDERS_DROPPED_RETRIED.
//
// Anything not a known handling note counts as SAFETY, so a flag the backend
// adds later warns until it is deliberately listed here.
export const SAFETY_FLAG_PREFIXES = [
  'NUMBER', 'UNIT', 'FREQUENCY', 'MEAL TIMING', 'DURATION', 'NEGATION', 'PERSON', 'NO TRANSLATION',
  'IDENTIFIER_NOT_RESTORED',
];
export const HANDLING_NOTES = ['ESCALATION_KEPT_IN_ENGLISH', 'PLACEHOLDERS_DROPPED_RETRIED'];

export function hasSafetyFlag(flags) {
  return (flags ?? []).some(f => !HANDLING_NOTES.includes(String(f).trim()));
}

/**
 * @param {string} text
 * @param {'in'|'out'} direction
 * @param {'ml'|'hi'} [targetLang]  required for "out"
 * @returns {Promise<{ok:boolean, text?:string, english?:string, flags?:string[],
 *   fallback?:boolean, disabled?:boolean, reason?:string}>}  never rejects
 */
export async function translate(text, direction, targetLang) {
  const jwt = localStorage.getItem('previu_token');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}/chat/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify({ text, direction, ...(targetLang ? { targetLang } : {}) }),
      signal: controller.signal,
    });
    if (!response.ok) return fallback(`HTTP_${response.status}`);
    const body = await response.json();
    if (body?.disabled) return { ok: false, disabled: true };
    if (!body?.ok || typeof body.text !== 'string' || !body.text.trim()) {
      return fallback(body?.reason || 'NOT_TRANSLATED');
    }
    return {
      ok: true,
      text: body.text,
      english: body.english,
      flags: Array.isArray(body.flags) ? body.flags : [],
    };
  } catch (err) {
    return fallback(err?.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK');
  } finally {
    clearTimeout(timer);
  }
}
